import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/buttons/Button';
import { Card } from '../../components/cards/Card';
import { Badge } from '../../components/common/Badge';
import { MapPin, CheckCircle, User, MessageCircle, X, ChevronRight, ChevronLeft, FileText, Gift, Loader2, Mail, Navigation, Share2, Heart, Check, Grid, Image as ImageIcon, Maximize2 } from 'lucide-react';
import { UserRole } from '../../types';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { generatePropertySlug, extractCityFromLocation } from '../../utils/seo/slugs';
import { SEOHead } from '../../components/seo/SEOHead';
import { Breadcrumb } from '../../components/seo/Breadcrumb';
import { generateRealEstateListingSchema } from '../../utils/seo/structuredData';
import { PropertyCard } from '../../components/cards/PropertyCard'; // Assuming we can use this for related properties, or just reuse Card logic

// Standard Leaflet Icon with Hue Shift to make it Red
// This ensures perfect centering unlike some custom PNGs
const standardRedIcon = L.icon({
   iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
   iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
   shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
   iconSize: [25, 41],
   iconAnchor: [12, 41],
   popupAnchor: [1, -34],
   tooltipAnchor: [16, -28],
   shadowSize: [41, 41],
   className: 'hue-rotate-120 saturate-150 contrast-125 filter-red-marker' // Custom class for color shift
});

// Component to handle map center and marker popup opening
const MapMarker = ({ position, title, location }: { position: [number, number], title: string, location: string }) => {
   const map = useMap();
   const markerRef = useRef<L.Marker>(null);

   useEffect(() => {
      if (position && position[0] && position[1]) {
         map.flyTo(position, 15, { animate: true, duration: 1.5 });

         // Force open popup after a slight delay to ensure map is ready
         setTimeout(() => {
            if (markerRef.current) {
               markerRef.current.openPopup();
            }
         }, 500);
      }
   }, [position, map]);

   return (
      <Marker ref={markerRef} position={position} icon={standardRedIcon}>
         <Popup autoPan={true}>
            <div className="font-bold text-sm text-gray-900">{title}</div>
            <div className="text-xs text-gray-500 mt-1 max-w-[150px]">{location}</div>
            <div className="text-[10px] text-[#2f5c96] font-semibold mt-1 flex items-center">
               <MapPin className="w-3 h-3 mr-1 fill-current" /> Exact Location
            </div>
         </Popup>
      </Marker>
   );
};

// --- Gallery Component ---
const PropertyGallery: React.FC<{ images: string[], onOpen: (idx: number) => void }> = ({ images, onOpen }) => {
   if (!images || images.length === 0) {
      return (
         <div className="h-64 sm:h-96 bg-gray-100 rounded-2xl flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-200">
            <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
            <span className="text-sm">No images available</span>
         </div>
      );
   }

   return (
      <div className="relative rounded-2xl overflow-hidden group">

         {/* Mobile: Horizontal Scroll Snap Carousel */}
         <div className="md:hidden">
            <div className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar h-72 sm:h-80">
               {images.map((img, idx) => (
                  <div key={idx} className="flex-shrink-0 w-full h-full snap-center relative" onClick={() => onOpen(idx)}>
                     <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                     <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full font-medium">
                        {idx + 1} / {images.length}
                     </div>
                  </div>
               ))}
            </div>
         </div>

         {/* Desktop: Adaptive Bento Grid */}
         <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-2 h-[450px]">
            {/* Main Image (Left Large) */}
            <div
               className={`relative cursor-pointer overflow-hidden ${images.length === 1 ? 'col-span-4 row-span-2 rounded-2xl' :
                  images.length === 2 ? 'col-span-2 row-span-2 rounded-l-2xl' :
                     'col-span-2 row-span-2 rounded-l-2xl'
                  }`}
               onClick={() => onOpen(0)}
            >
               <img src={images[0]} alt="Main Property" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
               <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors" />
            </div>

            {/* Secondary Images */}
            {images.slice(1, 5).map((img, idx) => {
               // Dynamic classes based on total count to fill the grid nicely
               let gridClass = 'col-span-1 row-span-1';
               if (images.length === 2) gridClass = 'col-span-2 row-span-2 rounded-r-2xl'; // 2nd image takes right half
               else if (images.length === 3) gridClass = 'col-span-2 row-span-1'; // 2 images on right stacked
               else if (images.length === 4 && idx === 0) gridClass = 'col-span-2 row-span-1'; // Top right wide

               // Border Radius logic for corners
               let roundedClass = '';
               if (images.length === 3) {
                  if (idx === 0) roundedClass = 'rounded-tr-2xl';
                  if (idx === 1) roundedClass = 'rounded-br-2xl';
               } else if (images.length >= 4) {
                  if (idx === 1 && images.length === 4) roundedClass = ''; // Middle one
                  if (idx === 0) roundedClass = images.length === 4 ? 'rounded-tr-2xl' : '';
                  if (idx === 1 && images.length === 5) roundedClass = 'rounded-tr-2xl';
                  if (idx === 3) roundedClass = 'rounded-br-2xl';
               }

               return (
                  <div
                     key={idx}
                     className={`relative cursor-pointer overflow-hidden bg-gray-100 ${gridClass} ${roundedClass}`}
                     onClick={() => onOpen(idx + 1)}
                  >
                     <img src={img} alt={`Detail ${idx + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />

                     {/* +More Overlay */}
                     {idx === 3 && images.length > 5 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center transition-opacity hover:bg-black/70">
                           <span className="text-white font-bold text-xl">+{images.length - 5} photos</span>
                        </div>
                     )}
                  </div>
               );
            })}
         </div>

         {/* Desktop "Show All" Button */}
         {images.length > 0 && (
            <button
               className="hidden md:flex absolute bottom-4 right-4 bg-white/90 hover:bg-white text-gray-800 px-4 py-2 rounded-lg text-sm font-semibold shadow-sm items-center gap-2 transition-all backdrop-blur-sm ring-1 ring-black/5"
               onClick={() => onOpen(0)}
            >
               <Maximize2 className="w-4 h-4" /> Show all photos
            </button>
         )}
      </div>
   );
};

export const AccommodationDetail: React.FC = () => {
   const { id, slug } = useParams<{ id?: string; slug?: string }>();
   const { properties, addBooking, validateReferralCode, users, savedProperties, toggleSavedProperty, sendMessage } = useData();
   const { user, isAuthenticated } = useAuth();
   const navigate = useNavigate();

   // Robust Property Lookup: Supports ID and Slug
   const property = properties.find(p => {
      // 1. Direct ID match (Legacy)
      if (id && p.id === id) return true;

      // 2. Slug match
      if (slug) {
         if (p.slug === slug) return true;
         // 3. Fallback: Check if ID fragment matches (last part of slug)
         // Slug format usually: title-location-fragID
         const parts = slug.split('-');
         const lastPart = parts[parts.length - 1];
         // Check if last part is a valid hex fragment (at least 6 chars) and property ID starts with it
         if (lastPart.length >= 6 && p.id.startsWith(lastPart)) return true;
      }
      return false;
   });

   // Calculate Derived Data
   const propertySlug = property ? (property.slug || generatePropertySlug(property.title, property.location, property.id)) : '';
   const city = property ? extractCityFromLocation(property.location) : '';

   // Related Properties Logic
   const relatedProperties = properties
      .filter(p => property && p.id !== property.id && p.status === 'AVAILABLE' && (
         p.location.includes(city) || // Same city
         (p.price >= property.price * 0.7 && p.price <= property.price * 1.3) // Similar price range
      ))
      .slice(0, 3);

   // Schema Data
   const schemaData = property ? generateRealEstateListingSchema({
      ...property,
      images: property.images || (property.image ? [property.image] : []),
      slug: propertySlug
   }, users.find(u => u.id === property.agentId)?.name) : null;

   // --- States ---
   const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
   const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
   const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
   const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
   const [messageText, setMessageText] = useState('Hi, is this property still available?');

   const [bookingStep, setBookingStep] = useState<'review' | 'success'>('review');
   const [referralCode, setReferralCode] = useState('');
   const [referralStatus, setReferralStatus] = useState<'none' | 'valid' | 'invalid'>('none');
   const [isBookingLoading, setIsBookingLoading] = useState(false);
   const [showCopied, setShowCopied] = useState(false);

   // Find the agent/landlord
   const agent = users.find(u => u.id === property?.agentId);
   const isSaved = property && savedProperties.includes(property.id);

   // Construct real image array
   const displayImages = property?.images && property.images.length > 0
      ? property.images
      : (property?.image ? [property.image] : []);

   // Determine Selection Options (Rooms OR Beds)
   let selectionOptions: { id: string, name: string, type: string, price: number, available: boolean, isBed: boolean }[] = [];

   if (property?.rooms && property.rooms.length > 0) {
      property.rooms.forEach(r => {
         selectionOptions.push({
            id: r.id,
            name: r.name,
            type: r.description || 'Standard Room',
            price: r.price,
            available: r.available !== false,
            isBed: false
         });
      });
   }

   if (property?.beds && property.beds.length > 0) {
      property.beds.forEach(b => {
         selectionOptions.push({
            id: b.id,
            name: b.name,
            type: 'Single Bed',
            price: b.price,
            available: b.available !== false,
            isBed: true
         });
      });
   }

   // Fallback if no rooms or beds
   if (selectionOptions.length === 0) {
      selectionOptions.push({
         id: 'whole-unit',
         name: 'Whole Unit',
         type: `${property?.bedrooms} Bedroom ${property?.type}`,
         price: property?.price || 0,
         available: property?.status === 'AVAILABLE',
         isBed: false
      });
   }

   const selectedUnit = selectionOptions.find(u => u.id === selectedRoomId);

   // Determine Overall Availability Badge
   const getAvailabilityBadge = () => {
      if (!property) return null;
      if (property.status === 'RENTED') return <Badge color="gray">Rented</Badge>;
      if (property.status === 'PENDING') return <Badge color="yellow">Pending</Badge>;

      // Dynamic Room/Bed Check
      const availableUnits = selectionOptions.filter(u => u.available !== false).length;
      if (availableUnits === 0) return <Badge color="gray">Rented Out</Badge>;
      if (availableUnits < selectionOptions.length) return <Badge color="yellow">{availableUnits} Units Left</Badge>;

      return <Badge color="green">Available</Badge>;
   };

   // --- Handlers ---

   const getWhatsAppLink = (customMsg?: string) => {
      if (!agent?.phoneNumber) return '#';
      const phone = agent.phoneNumber.replace(/[^0-9]/g, '');

      let msg = customMsg;
      if (!msg && property && selectedUnit && user) {
         msg = `*New Booking Request via Indingo Beans Rooms*\n\n` +
            `*Property:* ${property.title}\n` +
            `*Unit:* ${selectedUnit.name} (R${selectedUnit.price})\n\n` +
            `*Applicant Details:*\n` +
            `Name: ${user.name}\n` +
            `Phone: ${user.phoneNumber || 'N/A'}\n` +
            `Email: ${user.email}\n` +
            (referralCode ? `Referral Code: ${referralCode}\n` : '') +
            `\nHi, I have just submitted a booking request. Please send me the banking details or lease agreement to proceed.`;
      }
      return `https://wa.me/${phone}?text=${encodeURIComponent(msg || '')}`;
   };

   const handleSendMessage = async () => {
      if (!agent?.phoneNumber) {
         alert("Agent phone number not available.");
         return;
      }

      // 1. Sync Message to Internal DB (Shadow Message)
      if (user && agent) {
         try {
            // We prepend context so the agent sees which property this is about in their dashboard
            const internalContent = `[Inquiry: ${property?.title}] ${messageText}`;
            await sendMessage(agent.id, internalContent, property?.id);
         } catch (error) {
            console.error("Failed to sync message internally", error);
            // We continue to open WhatsApp even if internal sync fails
         }
      }

      // 2. Open WhatsApp
      const fullMessage = `Inquiry about *${property!.title}* in ${property!.location}.\n\n${messageText}`;
      window.open(getWhatsAppLink(fullMessage), '_blank');
      setIsMessageModalOpen(false);
   };

   const handleRoomSelect = (roomId: string) => {
      const targetUnit = selectionOptions.find(u => u.id === roomId);
      if (targetUnit && targetUnit.available === false) {
         // Cannot select unavailable unit
         return;
      }

      setSelectedRoomId(roomId);

      if (!isAuthenticated) {
         navigate('/login');
         return;
      }

      if (user?.role !== UserRole.STUDENT) {
         alert("Only students can book properties.");
         return;
      }

      // Open Modal
      setBookingStep('review');
      setReferralCode('');
      setReferralStatus('none');
      setIsBookingModalOpen(true);
   };

   const handleCheckReferral = () => {
      if (!referralCode) return;
      const referrer = validateReferralCode(referralCode);
      if (referrer && referrer.id !== user?.id) {
         setReferralStatus('valid');
      } else {
         setReferralStatus('invalid');
      }
   };

   const handleConfirmBooking = async () => {
      if (!property || !selectedUnit || !user) return;

      setIsBookingLoading(true);
      try {
         const success = await addBooking({
            propertyId: property.id,
            studentId: user.id,
            // Pass undefined if it's the virtual "whole-unit" option so the DB can insert NULL if needed
            roomId: (selectedUnit.id === 'whole-unit' || selectedUnit.isBed) ? undefined : selectedUnit.id,
            bedId: selectedUnit.isBed ? selectedUnit.id : undefined,
            referralCode: referralStatus === 'valid' ? referralCode : undefined
         });

         if (success) {
            setBookingStep('success');

            // Attempt auto-redirect
            if (agent?.phoneNumber) {
               setTimeout(() => {
                  const link = getWhatsAppLink();
                  const win = window.open(link, '_blank');
                  if (!win) {
                     console.warn("Popup blocked");
                  }
               }, 1500);
            }
         } else {
            alert("Booking failed. Please try again. (Database Error)");
         }
      } catch (error) {
         console.error("Booking submission error:", error);
         alert("An unexpected error occurred. Please check your internet connection.");
      } finally {
         setIsBookingLoading(false);
      }
   };

   const handleShare = () => {
      navigator.clipboard.writeText(window.location.href);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
   };

   if (properties.length === 0) {
      return (
         <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <div className="text-center">
               <Loader2 className="w-10 h-10 animate-spin text-[#2f5c96] mx-auto mb-4" />
               <p className="text-gray-500">Loading details...</p>
            </div>
         </div>
      );
   }

   if (!property) {
      return <div className="text-center py-20 text-gray-500">Property not found.</div>;
   }

   // Inject styles for map marker hue rotation
   const mapStyles = `
    .filter-red-marker {
       filter: hue-rotate(140deg) saturate(1.5) brightness(0.9);
    }
  `;

   return (
      <>
         {property && (
            <SEOHead
               title={`${property.title} in ${property.location} - Rent from R${property.price} | IndigoBeans`}
               description={`Rent ${property.title} in ${property.location}. ${property.type} with ${property.bedrooms} bedrooms. Verified student accommodation. Book now!`}
               canonical={`https://rooms.indigobeans.co.za/room/${propertySlug}`}
               ogImage={displayImages[0]}
               ogType="article"
               schema={schemaData ? [schemaData] : []}
            />
         )}
         <div className="min-h-screen bg-slate-50 font-sans pb-24 relative overflow-x-hidden">
            {/* Breadcrumb */}
            {property && (
               <div className="bg-white border-b border-gray-100">
                  <div className="max-w-7xl mx-auto px-4 py-2">
                     <Breadcrumb items={[
                        { name: 'Rooms', url: '/browse' },
                        { name: city, url: `/rooms/${city.toLowerCase()}` },
                        { name: property.title, url: `/room/${propertySlug}` }
                     ]} />
                  </div>
               </div>
            )}
            <style>{mapStyles}</style>

            {/* Lightbox */}
            {lightboxIndex !== null && (
               <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
                  <button onClick={() => setLightboxIndex(null)} className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full z-50">
                     <X className="w-8 h-8" />
                  </button>

                  {/* Lightbox Navigation - Left */}
                  <button
                     onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + displayImages.length) % displayImages.length); }}
                     className="absolute left-2 sm:left-4 text-white p-2 hover:bg-white/10 rounded-full z-50"
                  >
                     <ChevronLeft className="w-10 h-10" />
                  </button>

                  {/* Image Container */}
                  <div className="relative w-full h-full flex items-center justify-center">
                     <img
                        src={displayImages[lightboxIndex]}
                        className="max-h-[85vh] max-w-full object-contain shadow-2xl"
                        alt="Property Fullscreen"
                     />
                  </div>

                  {/* Lightbox Navigation - Right */}
                  <button
                     onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % displayImages.length); }}
                     className="absolute right-2 sm:right-4 text-white p-2 hover:bg-white/10 rounded-full z-50"
                  >
                     <ChevronRight className="w-10 h-10" />
                  </button>

                  {/* Counter */}
                  <div className="absolute bottom-6 text-white text-sm bg-black/50 px-4 py-2 rounded-full backdrop-blur-md">
                     {lightboxIndex + 1} / {displayImages.length}
                  </div>
               </div>
            )}

            {/* --- Message Modal --- */}
            {isMessageModalOpen && agent && (
               <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm">
                  <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 animate-scaleIn">
                     <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-lg flex items-center gap-2">
                           <MessageCircle className="w-5 h-5 text-green-500" />
                           Message Agent
                        </h3>
                        <button onClick={() => setIsMessageModalOpen(false)}><X className="w-5 h-5" /></button>
                     </div>

                     {/* Property Context for Message */}
                     <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Inquiry About</p>
                        <h4 className="font-bold text-gray-900">{property.title}</h4>
                        <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                           <span className="font-bold text-[#2f5c96]">R{property.price}/mo</span>
                           <span className="text-gray-300">|</span>
                           <span>{property.type}</span>
                           <span className="text-gray-300">|</span>
                           <span>{property.bedrooms} Bed, {property.bathrooms} Bath</span>
                        </div>
                        {selectedUnit && selectedUnit.id !== 'whole-unit' && (
                           <div className="mt-2 pt-2 border-t border-gray-100">
                              <span className="text-xs font-bold text-gray-400">UNIT:</span>
                              <span className="ml-2 text-xs font-bold text-blue-600 uppercase">{selectedUnit.name}</span>
                           </div>
                        )}
                     </div>

                     <p className="text-sm text-gray-500 mb-2">Send a message to <strong>{agent.name}</strong> via WhatsApp.</p>
                     <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded mb-3">
                        <span className="font-bold">Note:</span> This message will also be sent to the agent's dashboard inbox.
                     </div>
                     <textarea
                        className="w-full border p-3 rounded mb-4 focus:ring-2 focus:ring-green-500 outline-none"
                        rows={4}
                        value={messageText}
                        onChange={e => setMessageText(e.target.value)}
                     />
                     <Button onClick={handleSendMessage} className="w-full bg-green-500 hover:bg-green-600 border-none text-white shadow-lg shadow-green-200">
                        Send WhatsApp
                     </Button>
                  </div>
               </div>
            )}

            {/* --- Booking Confirmation Modal --- */}
            {isBookingModalOpen && selectedUnit && (
               <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
                  <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-scaleIn my-8">
                     <div className="bg-[#2f5c96] px-6 py-4 flex justify-between items-center text-white sticky top-0 z-10">
                        <h3 className="font-bold text-lg flex items-center gap-2">
                           {bookingStep === 'review' ? <><FileText className="w-5 h-5" /> Confirm Booking Details</> : <><CheckCircle className="w-5 h-5" /> Booking Successful</>}
                        </h3>
                        <button onClick={() => setIsBookingModalOpen(false)} className="hover:bg-white/10 rounded-full p-1"><X className="w-5 h-5" /></button>
                     </div>

                     {bookingStep === 'review' ? (
                        <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">

                           {/* Auto-filled Personal Details */}
                           <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                              <h4 className="text-xs font-bold text-[#2f5c96] uppercase mb-3">Applicant (You)</h4>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                 <div>
                                    <label className="block text-xs text-gray-500">Name</label>
                                    <div className="font-medium">{user?.name}</div>
                                 </div>
                                 <div>
                                    <label className="block text-xs text-gray-500">Phone</label>
                                    <div className="font-medium">{user?.phoneNumber || 'N/A'}</div>
                                 </div>
                                 <div className="col-span-2">
                                    <label className="block text-xs text-gray-500">Email</label>
                                    <div className="font-medium">{user?.email}</div>
                                 </div>
                              </div>
                           </div>

                           {/* Property Summary */}
                           <div className="border-t border-b border-gray-100 py-4 space-y-2">
                              <div className="flex justify-between text-sm">
                                 <span className="text-gray-600">Property</span>
                                 <span className="font-medium text-gray-900">{property.title}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                 <span className="text-gray-600">Selected Unit</span>
                                 <span className="font-medium text-gray-900">{selectedUnit.name}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                 <span className="text-gray-600">Monthly Rent</span>
                                 <span className="font-bold text-[#2f5c96]">R{selectedUnit.price}</span>
                              </div>
                           </div>

                           {/* Referral Code */}
                           <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                              <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center">
                                 <Gift className="w-4 h-4 mr-2 text-[#c19cca]" /> Referral Code
                              </h4>
                              <div className="flex gap-2">
                                 <input
                                    type="text"
                                    placeholder="Friend's Code"
                                    className="flex-1 p-2 border border-gray-300 rounded text-sm uppercase"
                                    value={referralCode}
                                    onChange={(e) => { setReferralCode(e.target.value); setReferralStatus('none'); }}
                                 />
                                 <Button size="sm" variant="outline" onClick={handleCheckReferral}>Apply</Button>
                              </div>
                              {referralStatus === 'valid' && <p className="text-xs text-green-600 mt-2">Code applied! Friend will receive rewards.</p>}
                              {referralStatus === 'invalid' && <p className="text-xs text-red-500 mt-2">Invalid code.</p>}
                           </div>

                           <Button className="w-full py-3 shadow-md" onClick={handleConfirmBooking} disabled={isBookingLoading}>
                              {isBookingLoading ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Processing...</> : 'Confirm & Submit Request'}
                           </Button>
                        </div>
                     ) : (
                        <div className="p-8 text-center animate-fadeIn">
                           <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                              <CheckCircle className="w-10 h-10 text-green-600" />
                           </div>
                           <h3 className="text-2xl font-bold text-gray-900 mb-2">Success!</h3>
                           <p className="text-gray-600 mb-6">Your booking request has been recorded. To finalize the lease and banking details, please connect with the agent on WhatsApp.</p>

                           <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="block w-full mb-3">
                              <Button className="w-full bg-green-500 text-white hover:bg-green-600 border-none shadow-lg shadow-green-200">
                                 <MessageCircle className="w-5 h-5 mr-2" /> Continue to WhatsApp
                              </Button>
                           </a>

                           <Button variant="outline" className="w-full" onClick={() => navigate('/student/dashboard')}>
                              Go to Dashboard
                           </Button>
                        </div>
                     )}
                  </div>
               </div>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

               {/* Gallery */}
               <div className="mb-8">
                  <PropertyGallery images={displayImages} onOpen={setLightboxIndex} />
               </div>

               <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">

                  {/* Left Column */}
                  <div className="lg:col-span-2 space-y-8">
                     <div className="bg-white rounded-2xl p-4 sm:p-8 border border-gray-100 shadow-sm relative">
                        {/* Action Buttons: Save & Share */}
                        <div className="absolute top-4 right-4 sm:top-8 sm:right-8 flex gap-2 z-10">
                           <button
                              onClick={() => isAuthenticated ? toggleSavedProperty(property.id) : navigate('/login')}
                              className={`p-2 rounded-full border transition-colors ${isSaved ? 'bg-red-50 border-red-200 text-red-500' : 'bg-white border-gray-200 text-gray-400 hover:text-red-500'}`}
                              title={isSaved ? "Saved" : "Save Property"}
                           >
                              <Heart className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
                           </button>
                           <button
                              onClick={handleShare}
                              className="p-2 rounded-full border border-gray-200 bg-white text-gray-400 hover:text-[#2f5c96] hover:bg-blue-50 transition-colors"
                              title="Share Property"
                           >
                              {showCopied ? <Check className="w-5 h-5 text-green-500" /> : <Share2 className="w-5 h-5" />}
                           </button>
                           {showCopied && <div className="absolute top-12 right-0 bg-black/80 text-white text-xs px-2 py-1 rounded">Link Copied!</div>}
                        </div>

                        <div className="flex justify-between items-start mb-6 pr-16 sm:pr-24">
                           <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                                 <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 break-words">{property.title}</h1>
                                 {/* Header Status Badge */}
                                 {getAvailabilityBadge()}
                              </div>
                              <div className="flex flex-col gap-1">
                                 {property.locationName && (
                                    <div className="text-[#2f5c96] font-bold text-lg mb-1">{property.locationName}</div>
                                 )}
                                 <div className="flex items-start text-gray-500 text-sm sm:text-base">
                                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#2f5c96] flex-shrink-0 mt-0.5" />
                                    <span className="break-words">{property.location}</span>
                                 </div>
                              </div>
                           </div>
                        </div>

                        <div className="flex items-center justify-between lg:hidden mb-6">
                           <div>
                              <p className="text-3xl font-bold text-[#2f5c96]">R{property.price}</p>
                              <p className="text-gray-500">per month (from)</p>
                           </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 sm:gap-6 py-4 sm:py-6 border-y border-gray-100 mb-6">
                           <div className="text-center">
                              <p className="text-gray-500 text-[10px] sm:text-xs uppercase tracking-wider mb-1">Type</p>
                              <p className="font-semibold text-sm sm:text-lg text-gray-900 truncate">{property.type}</p>
                           </div>
                           <div className="text-center border-l border-gray-100">
                              <p className="text-gray-500 text-[10px] sm:text-xs uppercase tracking-wider mb-1">Bedrooms</p>
                              <p className="font-semibold text-sm sm:text-lg text-gray-900">{property.bedrooms}</p>
                           </div>
                           <div className="text-center border-l border-gray-100">
                              <p className="text-gray-500 text-[10px] sm:text-xs uppercase tracking-wider mb-1">Bathrooms</p>
                              <p className="font-semibold text-sm sm:text-lg text-gray-900">{property.bathrooms}</p>
                           </div>
                        </div>

                        <h2 className="text-lg sm:text-xl font-bold mb-4 text-gray-900">Description</h2>
                        <p className="text-gray-600 leading-relaxed mb-8 text-sm sm:text-lg whitespace-pre-wrap break-words">
                           {property.description}
                        </p>

                        <h2 className="text-lg sm:text-xl font-bold mb-4 text-gray-900">Amenities</h2>
                        <div className="flex flex-wrap gap-2 sm:gap-3 mb-8">
                           {property.amenities.map(amenity => (
                              <Badge key={amenity} color="blue" className="text-xs sm:text-sm py-1 px-2 sm:px-3">{amenity}</Badge>
                           ))}
                        </div>

                        {/* Map Section */}
                        {property.latitude && property.longitude && (
                           <div className="mb-8 border-t border-gray-100 pt-8">
                              <div className="flex justify-between items-center mb-4">
                                 <h2 className="text-xl font-bold text-gray-900">Location</h2>
                                 <span className="text-sm text-gray-500 flex items-center"><MapPin className="w-4 h-4 mr-1 text-red-500" /> Precise Location</span>
                              </div>
                              <div className="h-96 w-full rounded-xl overflow-hidden border border-gray-200 shadow-md z-0 relative">
                                 <MapContainer
                                    center={[property.latitude, property.longitude]}
                                    zoom={15}
                                    scrollWheelZoom={false}
                                    className="h-full w-full"
                                    key={`${property.latitude}-${property.longitude}`} // Force re-render if coords change
                                 >
                                    <TileLayer
                                       attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                       url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    <MapMarker
                                       position={[property.latitude, property.longitude]}
                                       title={property.title}
                                       location={property.location}
                                    />
                                 </MapContainer>
                              </div>
                              <div className="mt-4 flex flex-col sm:flex-row justify-between items-center bg-gray-50 p-4 rounded-lg border border-gray-200 gap-4">
                                 <div className="flex items-center text-gray-700">
                                    <MapPin className="w-5 h-5 mr-2 text-[#2f5c96]" />
                                    <span className="font-medium">{property.location}</span>
                                 </div>
                                 <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${property.latitude},${property.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full sm:w-auto"
                                 >
                                    <Button size="sm" className="flex items-center justify-center w-full bg-[#2f5c96] hover:bg-[#254a7a] text-white">
                                       <Navigation className="w-4 h-4 mr-2" /> View Directions
                                    </Button>
                                 </a>
                              </div>
                           </div>
                        )}
                     </div>

                     {/* Unit Selection (Rooms & Beds) */}
                     <div>
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Select a Room or Bed</h2>
                        <div className="space-y-4">
                           {selectionOptions.map((unit) => {
                              const isAvailable = unit.available !== false;
                              return (
                                 <Card
                                    key={unit.id}
                                    className={`p-3 sm:p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 transition-all group ${!isAvailable
                                       ? 'bg-gray-100 border-gray-200 opacity-75'
                                       : 'cursor-pointer hover:border-[#2f5c96] hover:shadow-md bg-white'
                                       } ${selectedRoomId === unit.id ? 'border-2 border-[#2f5c96] bg-blue-50/20' : ''}`}
                                    onClick={() => isAvailable && handleRoomSelect(unit.id)}
                                 >
                                    <div className="flex items-center gap-4">
                                       <div className={`p-2 rounded-lg ${unit.isBed ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                                          {unit.isBed ? <Grid className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                                       </div>
                                       <div>
                                          <div className="flex items-center gap-2">
                                             <h4 className={`font-bold ${!isAvailable ? 'text-gray-500 decoration-gray-400' : 'text-gray-900 group-hover:text-[#2f5c96]'}`}>
                                                {unit.name}
                                             </h4>
                                             {selectedRoomId === unit.id && <Badge color="blue">Selected</Badge>}
                                             {!isAvailable && <span className="text-xs font-bold text-white bg-gray-400 px-2 py-0.5 rounded">TAKEN</span>}
                                          </div>
                                          <p className="text-sm text-gray-500">{unit.type}</p>
                                       </div>
                                    </div>
                                    <div className="text-right">
                                       <p className={`font-bold text-lg ${!isAvailable ? 'text-gray-400' : 'text-[#2f5c96]'}`}>
                                          R{unit.price}<span className="text-xs text-gray-400 font-normal">/mo</span>
                                       </p>
                                       <Button
                                          size="sm"
                                          className={`mt-1 ${!isAvailable ? 'bg-gray-300 text-gray-500 border-transparent cursor-not-allowed hover:bg-gray-300 shadow-none' : ''}`}
                                          variant={selectedRoomId === unit.id ? 'primary' : 'outline'}
                                          disabled={!isAvailable}
                                       >
                                          {!isAvailable ? 'Unavailable' : (selectedRoomId === unit.id ? 'Book Now' : 'Select')}
                                       </Button>
                                    </div>
                                 </Card>
                              );
                           })}
                        </div>
                     </div>

                     {/* Host Details Bottom Section */}
                     {agent && (
                        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm flex flex-col sm:flex-row items-center gap-6">
                           <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                              {agent.avatar ? <img src={agent.avatar} className="w-full h-full object-cover" /> : <User className="w-8 h-8 text-gray-400" />}
                           </div>
                           <div className="flex-1 text-center sm:text-left">
                              <h3 className="text-lg font-bold text-gray-900">{agent.name}</h3>
                              <p className="text-sm text-gray-500 capitalize mb-1">{agent.role}</p>

                              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-sm text-gray-600 justify-center sm:justify-start">
                                 <div className="flex items-center max-w-full">
                                    <Mail className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0" />
                                    <span className="truncate text-xs sm:text-sm">{agent.email}</span>
                                 </div>
                                 {agent.isTrusted && (
                                    <div className="flex items-center text-green-600 bg-green-50 px-2 py-0.5 rounded-full text-xs">
                                       <CheckCircle className="w-3 h-3 mr-1" /> Verified Partner
                                    </div>
                                 )}
                              </div>
                           </div>
                           <Button variant="outline" onClick={() => setIsMessageModalOpen(true)}>Contact Agent</Button>
                        </div>
                     )}
                  </div>

                  {/* Right Column: Sticky Summary */}
                  <div className="space-y-6">
                     <div className="hidden lg:block sticky top-24 space-y-6">
                        <Card className="p-6 border-t-4 border-t-[#2f5c96] shadow-lg">
                           <h3 className="font-bold text-lg mb-4 text-gray-900">Interested?</h3>
                           <div className="space-y-4">
                              <div className="bg-gray-50 p-4 rounded-lg text-center">
                                 <p className="text-sm text-gray-600 mb-1">Rent starts from</p>
                                 <span className="text-3xl font-bold text-[#2f5c96]">R{property.price}</span>
                                 <span className="text-gray-500"> / month</span>
                              </div>

                              <Button
                                 className="w-full py-3 text-lg"
                                 onClick={() => handleRoomSelect(selectedRoomId || selectionOptions.find(u => u.available !== false)?.id || '')}
                                 disabled={selectionOptions.every(u => u.available === false)}
                              >
                                 {isAuthenticated ? 'Book Now' : 'Log in to Book'}
                              </Button>
                              <Button variant="outline" className="w-full" onClick={() => setIsMessageModalOpen(true)}>
                                 <MessageCircle className="w-4 h-4 mr-2" /> Message Host
                              </Button>
                           </div>
                           <p className="text-xs text-gray-400 text-center mt-4">You won't be charged yet.</p>
                        </Card>

                        {/* Host Information Card (Sidebar) */}
                        {agent ? (
                           <Card className="p-6">
                              <h3 className="font-bold text-lg mb-4 text-gray-900">Hosted By</h3>
                              <div className="flex items-center gap-4 mb-4">
                                 <div className="w-14 h-14 bg-[#2f5c96]/10 rounded-full flex items-center justify-center border-2 border-white shadow-sm overflow-hidden">
                                    {agent.avatar ? <img src={agent.avatar} className="w-full h-full object-cover" /> : <User className="w-6 h-6 text-[#2f5c96]" />}
                                 </div>
                                 <div>
                                    <p className="font-bold text-gray-900">{agent.name}</p>
                                    <p className="text-sm text-gray-500 capitalize">{agent.role}</p>
                                 </div>
                              </div>

                              <div className="space-y-2 mb-4">
                                 {agent.isTrusted && (
                                    <div className="flex items-center text-sm text-green-600 bg-green-50 p-2 rounded-lg">
                                       <CheckCircle className="w-4 h-4 mr-2" /> Verified Partner
                                    </div>
                                 )}
                              </div>
                           </Card>
                        ) : (
                           <Card className="p-6">
                              <p className="text-gray-500 text-center">Host information unavailable.</p>
                           </Card>
                        )}
                     </div>
                  </div>

               </div>

               {/* --- Related Properties Section --- */}
               {relatedProperties.length > 0 && (
                  <div className="py-8 sm:py-12 border-t border-gray-200 mt-8 sm:mt-12">
                     <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">More Rooms in {city}</h2>
                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {relatedProperties.map(rp => (
                           // Reusing Basic Card for Related Property Preview
                           <Card key={rp.id} className="cursor-pointer hover:shadow-lg transition-all group overflow-hidden" onClick={() => navigate(`/room/${rp.slug || generatePropertySlug(rp.title, rp.location, rp.id)}`)}>
                              <div className="h-48 overflow-hidden relative">
                                 <img src={rp.image || (rp.images && rp.images[0])} alt={rp.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                 <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded font-bold">
                                    R{rp.price}/mo
                                 </div>
                              </div>
                              <div className="p-4">
                                 <h3 className="font-bold text-gray-900 truncate mb-1">{rp.title}</h3>
                                 <div className="flex items-center text-xs text-gray-500 mb-3">
                                    <MapPin className="w-3 h-3 mr-1" /> {rp.location}
                                 </div>
                                 <div className="flex gap-2 text-xs text-gray-600">
                                    <span>{rp.bedrooms} Bed</span> • <span>{rp.bathrooms} Bath</span> • <span className="capitalize">{rp.type}</span>
                                 </div>
                              </div>
                           </Card>
                        ))}
                     </div>
                  </div>
               )}
            </div>
         </div>
      </>
   );
}